import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

import {
  AnalysisChartFilterData,
  PeriodOptionType,
  TimeUnitType,
} from "../../../models/ChartCollectionData";
import {
  PeriodType,
  transformToStackBarChartData as transformToChartData,
} from "../../../models/OrgHealthChartData";
import DataSvc from "../../../services/dataSvc";
import { TimeUnitSelect } from "../../AnalysisCollectionsComponents/CollectionChartArea/LineChart/TimeUnitSelect";
import { DateRangeArea } from "../../AnalysisCollectionsComponents/DateRangeArea";
import { OrgHealthLineChart } from "../OrgHealthLineChart";

import "./styles.scss";

interface IOrgHealthIndexOverviewSection {
  organizationId: string | number;
  period?: PeriodType;
}

export const OrgHealthIndexOverviewSection: React.FC<
  IOrgHealthIndexOverviewSection
> = ({ organizationId, period: initialPeriod = PeriodType.DAY }) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`dashboardPage.orgHealthIndexOverviewSection.${key}`);

  const [timeUnit, setTimeUnit] = useState<PeriodType>(initialPeriod);

  const chartRef = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(1500);

  useEffect(() => {
    const updateChartWidth = () => {
      if (chartRef.current) {
        const containerWidth = chartRef.current.offsetWidth;
        setChartWidth(containerWidth > 0 ? containerWidth - 40 : 1500);
      }
    };

    updateChartWidth();

    window.addEventListener("resize", updateChartWidth);
    return () => {
      window.removeEventListener("resize", updateChartWidth);
    };
  }, []);

  const [endDate, setEndDate] = useState<Date>(
    moment().startOf("month").toDate()
  );
  const [startDate, setStartDate] = useState<Date>(
    moment().startOf("month").subtract(1, "months").toDate()
  );

  type CustomChartDataType = {
    id: number;
    name: string;
    collection_id: number;
    template_id: number;
    template_name: string;
    period_option: PeriodOptionType;
    period_start: string;
    period_end: string;
    type: "line" | "bar" | "pie" | "table";
    time_unit: TimeUnitType;
    compare_to_prev: boolean;
    metrics: string;
    aggregation: "sum" | "avg";
    grouping1: string;
    grouping2: string;
    prefecture_filter: string;
    municipality_filter: string | null;
    category_filter: string | null;
    label_filter: string | null;
    display_order: number;
    is_template: true;
    created_at: string;
    created_by: number;
    last_modified_at: string;
    last_modified_by: number;
  };

  const [chartData, setChartData] = useState<CustomChartDataType>({
    id: 999,
    name: "Facility Status",
    collection_id: 0,
    template_id: 0,
    template_name: "",
    period_option: "custom",
    period_start: moment()
      .startOf("month")
      .subtract(1, "months")
      .toDate()
      .toISOString(),
    period_end: moment().startOf("month").toDate().toISOString(),
    type: "bar",
    time_unit: initialPeriod,
    compare_to_prev: false,
    metrics: JSON.stringify(["facility_status"]),
    aggregation: "sum",
    grouping1: "",
    grouping2: "",
    prefecture_filter: "",
    municipality_filter: null,
    category_filter: null,
    label_filter: null,
    display_order: 0,
    is_template: true,
    created_at: new Date().toISOString(),
    created_by: 0,
    last_modified_at: new Date().toISOString(),
    last_modified_by: 0,
  });

  const {
    data: locationStatsHistorical,
    isLoading,
    refetch,
  } = useQuery(
    ["locationStatsHistorical", organizationId, timeUnit, startDate, endDate],
    async () => {
      if (!organizationId) return null;

      const response = await DataSvc.getLocationStatsHistorical({
        organization_id: Number(organizationId),
        start_date: startDate,
        end_date: endDate,
        period: timeUnit,
      });

      return response;
    },
    {
      enabled: !!organizationId,
    }
  );

  const handleTimeUnitChange = (newTimeUnit: "day" | "week" | "month") => {
    setTimeUnit(newTimeUnit as PeriodType);
    setChartData((prev: CustomChartDataType) => ({
      ...prev,
      time_unit: newTimeUnit as TimeUnitType,
      last_modified_at: new Date().toISOString(),
    }));
    setTimeout(() => {
      refetch();
    }, 100);
  };

  const handleDateRangeChange = (start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
    setChartData((prev: CustomChartDataType) => ({
      ...prev,
      period_start: start.toISOString(),
      period_end: end.toISOString(),
      last_modified_at: new Date().toISOString(),
    }));
    setTimeout(() => {
      refetch();
    }, 100);
  };

  if (isLoading || !locationStatsHistorical) {
    return (
      <div className="facility-status-section">
        <div className="loading-indicator">{t("loading")}</div>
      </div>
    );
  }

  const ratingDataSources = [
    {
      label: t("score_D"),
      fieldName: "D",
      style: "solid",
      color: "#F44336",
    },
    {
      label: t("score_C"),
      fieldName: "C",
      style: "solid",
      color: "#FF9800",
    },
    {
      label: t("score_B"),
      fieldName: "B",
      style: "solid",
      color: "#FFEB3B",
    },
    {
      label: t("score_A"),
      fieldName: "A",
      style: "solid",
      color: "#4CAF50",
    },
    {
      label: t("total"),
      fieldName: "TOTAL",
      style: "solid",
      color: "#2196F3",
    },
  ];

  const transformedChartData = transformToChartData(
    locationStatsHistorical,
    t("facility_ratings_over_time"),
    ratingDataSources
  );

  const handleDateRangeApply = (formData: AnalysisChartFilterData) => {
    const newStartDate = formData.fromDate
      ? new Date(formData.fromDate)
      : startDate;
    const newEndDate = formData.toDate ? new Date(formData.toDate) : endDate;

    setChartData((prev: CustomChartDataType) => ({
      ...prev,
      period_option: formData.periodDropdownValue,
      period_start: newStartDate.toISOString(),
      period_end: newEndDate.toISOString(),
      last_modified_at: new Date().toISOString(),
    }));

    handleDateRangeChange(newStartDate, newEndDate);
  };

  return (
    <div className="facility-status-section">
      <div className="chart-container">
        <div className="controls-container">
          <div className="placeholder"></div>
          <h3 className="chart-title">{t("facility_ratings_over_time")}</h3>
          <div className="control-group">
            <DateRangeArea
              chartData={chartData as any}
              onClickApply={handleDateRangeApply}
            />
            <div className="time-unit-control">
              <TimeUnitSelect
                timeUnit={timeUnit as "day" | "week" | "month"}
                setTimeUnit={handleTimeUnitChange}
              />
            </div>
          </div>
        </div>
        <div className="chart-area" ref={chartRef}>
          <OrgHealthLineChart
            width={chartWidth}
            height={400}
            chartData={transformedChartData}
          />
        </div>
      </div>
    </div>
  );
};

export default OrgHealthIndexOverviewSection;
